import React, { useEffect, useState } from 'react';
import Modal from './Modal';
import {
  listZocsAppointments,
  previewZocsSync,
  syncZocsAppointment,
} from '../api/skipzyAdmin';

// Same screen as the admin panel's "ZOCS sync": ZOCS appointments, whether the
// customer app already has each one, a preview of what a sync records and
// credits, then the sync itself. The Skipzy backend does the work — it reads
// ZOCS itself and never writes back here.

const PAGE_SIZE = 50;
const STATUSES = ['scheduled', 'queued', 'pack_assigned', 'dispatched', 'in_progress', 'completed'];
const EMPTY_FILTERS = { phone: '', date_from: '', date_to: '', include_hidden: false };
const METHOD_LABEL = { cash: 'Cash', upi: 'UPI', online: 'Online', zocs: 'Not reported by ZOCS' };

function errorMessage(err, fallback) {
  const detail = err.response?.data?.detail;
  if (!detail) return err.message || fallback;
  if (typeof detail === 'string') return detail;
  if (detail.message) return typeof detail.message === 'string' ? detail.message : JSON.stringify(detail.message);
  return JSON.stringify(detail);
}

const rupees = (paise) => `₹${((paise || 0) / 100).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
const syncable = (row) => !row.synced && !row.sync_blocker && row.phone && row.scheduled_date;

function syncSummary(result) {
  const payments = result.orders_backfilled
    ? `${result.orders_backfilled} payment${result.orders_backfilled === 1 ? '' : 's'} found in ZOCS`
    : null;
  if (!result.created) return payments || 'No new payments in ZOCS';
  return [
    result.user_created ? 'New customer created' : 'Added to existing customer',
    payments,
    result.notified ? 'push sent' : null,
  ].filter(Boolean).join(', ');
}

function Pill({ children, tone = 'gray' }) {
  const tones = {
    gray: 'bg-gray-100 text-gray-600',
    green: 'bg-green-50 text-green-700',
    pink: 'bg-pink-50 text-pink-700',
    blue: 'bg-blue-50 text-blue-700',
  };
  return <span className={`inline-block text-xs px-2 py-0.5 rounded-full ${tones[tone]}`}>{children}</span>;
}

function PreviewModal({ row, onClose, onSynced }) {
  const [p, setP] = useState(null);
  const [error, setError] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    previewZocsSync(row.booking_code, row.phone, row.scheduled_date)
      .then(setP)
      .catch(err => setError(errorMessage(err, 'Failed to load preview')));
  }, [row]);

  async function confirm() {
    setSyncing(true);
    setError(null);
    try {
      const result = await syncZocsAppointment(row.booking_code, row.phone, row.scheduled_date);
      setDone(syncSummary(result));
      onSynced();
    } catch (err) {
      setError(errorMessage(err, 'Sync failed'));
    } finally {
      setSyncing(false);
    }
  }

  const title = row.synced ? `Check payments · ${row.booking_code}` : `Review sync · ${row.booking_code}`;
  const nothingToDo = p && p.already_synced && p.orders.length === 0;

  return (
    <Modal title={title} onClose={onClose} wide>
      <p className="text-xs text-gray-400 mb-4">Visit {fmtDate(row.scheduled_date)} · nothing is written until you confirm</p>
      {error && <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>}
      {!p && !error && <p className="text-sm text-gray-500">Loading…</p>}
      {p && (
        <div className="space-y-4 text-sm">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="border border-gray-200 rounded p-3 space-y-1">
              <h4 className="text-xs font-bold text-gray-500 uppercase">Customer</h4>
              <p className="font-medium">{p.customer_name || 'Unnamed'}</p>
              <p className="text-gray-500">{p.phone_10}</p>
              <p>{p.has_app_account
                ? `Existing app account · ${p.coin_balance ?? 0} coins now`
                : 'No app account yet — one will be created'}</p>
              {p.status_after
                ? <p>Booking will be <Pill tone="blue">{p.status_after.replace(/_/g, ' ')}</Pill></p>
                : <p className="text-gray-500">Already in the app.</p>}
              {p.push_on_sync && <p className="text-gray-500">Sends the “we found your home trial” push.</p>}
            </div>
            <div className="border border-gray-200 rounded p-3 space-y-1">
              <h4 className="text-xs font-bold text-gray-500 uppercase">Amount to record</h4>
              <p className="text-2xl font-semibold">{rupees(p.amount_paise)}</p>
              <p className="text-gray-500">
                {p.orders.length === 0
                  ? (p.already_synced ? 'Every payment ZOCS has is already in the app.' : 'Nothing paid in ZOCS yet — syncs without an order.')
                  : `${p.orders.length} paid order${p.orders.length === 1 ? '' : 's'} from ZOCS, recorded as paid`}
              </p>
              {p.orders_error && <p className="text-red-600">Couldn’t read payments from ZOCS: {p.orders_error}</p>}
            </div>
            <div className="border border-gray-200 rounded p-3 space-y-1">
              <h4 className="text-xs font-bold text-gray-500 uppercase">Coins</h4>
              {!p.coins_enabled ? (
                <p className="text-gray-500">Coins are paused — none will be credited.</p>
              ) : (
                <>
                  <p className="text-2xl font-semibold">+{p.coins_to_customer}</p>
                  <div className="flex justify-between"><span className="text-gray-500">On the amount paid</span><span>+{p.order_coins}</span></div>
                  {p.milestones.map(m => (
                    <div key={m.label} className="flex justify-between">
                      <span className="text-gray-500">{m.label} milestone{m.tiers > 1 ? ` × ${m.tiers}` : ''}</span>
                      <span>+{m.coins}</span>
                    </div>
                  ))}
                  {p.referrer && (
                    <p className="text-gray-500 border-t pt-1">
                      Also +{p.referrer.coins} to {p.referrer.name || 'their referrer'} ({p.referrer.phone_10}) for referring them
                      {p.referrer.credit_in_days ? `, credited ${p.referrer.credit_in_days} days after this order.` : '.'}
                    </p>
                  )}
                </>
              )}
            </div>
          </div>

          {p.orders.map(order => (
            <div key={order.source_order_id} className="border border-gray-200 rounded p-3">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <span className="font-mono text-xs">{order.source_order_id}</span>
                <span className="text-xs text-gray-500">
                  <Pill>{METHOD_LABEL[order.payment_method] || order.payment_method}</Pill>
                  {order.paid_at && <span className="ml-2">Paid {fmtDate(order.paid_at)}</span>}
                </span>
              </div>
              {order.items.length === 0
                ? <p className="text-gray-500">ZOCS didn’t match any items to this order.</p>
                : (
                  <ul className="divide-y divide-gray-100">
                    {order.items.map((item, i) => (
                      <li key={i} className="flex justify-between py-1">
                        <span>
                          {item.product_title}
                          <span className="text-xs text-gray-400 ml-2">{[item.brand, item.size_label].filter(Boolean).join(' · ')}</span>
                        </span>
                        <span>{rupees(item.price_paise)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              <div className="border-t border-gray-100 mt-2 pt-2 space-y-0.5">
                <div className="flex justify-between"><span className="text-gray-500">Item total</span><span>{rupees(order.subtotal_paise)}</span></div>
                {order.discount_paise > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">{order.discount_code ? `Coupon ${order.discount_code}` : 'Discount (no code reported)'}</span>
                    <span>− {rupees(order.discount_paise)}</span>
                  </div>
                )}
                <div className="flex justify-between font-medium"><span>Paid</span><span>{rupees(order.amount_paise)}</span></div>
              </div>
            </div>
          ))}

          <div className="flex justify-end pt-2">
            {done ? (
              <p className="text-green-700">✓ {done}</p>
            ) : p.sync_blocker ? (
              <p className="text-red-600">Can’t sync: {p.sync_blocker}</p>
            ) : nothingToDo ? (
              <p className="text-gray-500">Nothing new to record.</p>
            ) : (
              <button onClick={confirm} disabled={syncing}
                className="bg-zoddle-green text-white text-sm font-semibold px-4 py-2 rounded-lg hover:opacity-90 disabled:opacity-50">
                {syncing ? (p.already_synced ? 'Checking…' : 'Syncing…') : (p.already_synced ? 'Refresh payments' : 'Confirm sync')}
              </button>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}

export default function ZocsSyncSection() {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [applied, setApplied] = useState(EMPTY_FILTERS);
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [previewRow, setPreviewRow] = useState(null);
  const [bulkSyncing, setBulkSyncing] = useState(false);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const params = { page, page_size: PAGE_SIZE, include_hidden: applied.include_hidden };
      if (applied.phone.trim()) params.phone = applied.phone.trim();
      if (applied.date_from) params.date_from = applied.date_from;
      if (applied.date_to) params.date_to = applied.date_to;
      if (status) params.operational_status = status;
      setData(await listZocsAppointments(params));
    } catch (err) {
      setError(errorMessage(err, 'Failed to load ZOCS appointments'));
    } finally {
      setLoading(false);
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [applied, status, page]);

  const rows = data?.items || [];
  const pending = rows.filter(syncable);

  // One at a time so a failure names its code and the rest still go through.
  async function syncPage() {
    if (!pending.length || !window.confirm(`Sync ${pending.length} appointment(s) on this page to the app?`)) return;
    setBulkSyncing(true);
    setMessage(null);
    let synced = 0;
    const failed = [];
    for (const row of pending) {
      try {
        if ((await syncZocsAppointment(row.booking_code, row.phone, row.scheduled_date)).created) synced += 1;
      } catch (err) {
        failed.push(`${row.booking_code}: ${errorMessage(err, 'failed')}`);
      }
    }
    setBulkSyncing(false);
    setMessage(failed.length
      ? { ok: false, text: `Synced ${synced}; failed ${failed.length} — ${failed.join(' · ')}` }
      : { ok: true, text: `Synced ${synced} of ${pending.length}` });
    load();
  }

  const input = 'border border-gray-200 rounded px-3 py-2 text-sm';

  return (
    <section className="mb-8">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-1">
        <h3 className="text-sm font-bold text-gray-600 uppercase tracking-wide">ZOCS Sync</h3>
        <button onClick={syncPage} disabled={bulkSyncing || pending.length === 0}
          className="border border-gray-300 text-sm font-semibold px-3 py-1.5 rounded-lg hover:bg-gray-50 disabled:opacity-50">
          {bulkSyncing ? 'Syncing…' : `Sync ${pending.length} on this page`}
        </button>
      </div>
      <p className="text-sm text-gray-500 mb-4">
        Copy ZOCS appointments into the customer app.
        {data && ` ${data.total} in ZOCS · ${pending.length} on this page not yet in the app.`}
      </p>

      {error && <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>}
      {message && (
        <div className={`mb-4 text-sm rounded px-3 py-2 border ${message.ok ? 'text-green-700 bg-green-50 border-green-200' : 'text-red-700 bg-red-50 border-red-200'}`}>
          {message.text}
        </div>
      )}

      <form
        onSubmit={e => { e.preventDefault(); setPage(1); setApplied(filters); }}
        className="flex flex-wrap items-end gap-3 mb-2"
      >
        <label className="text-xs text-gray-500">Phone
          <input type="search" value={filters.phone} placeholder="10-digit phone"
            onChange={e => setFilters(f => ({ ...f, phone: e.target.value }))} className={`${input} block w-44 mt-1`} />
        </label>
        <label className="text-xs text-gray-500">From
          <input type="date" value={filters.date_from}
            onChange={e => setFilters(f => ({ ...f, date_from: e.target.value }))} className={`${input} block mt-1`} />
        </label>
        <label className="text-xs text-gray-500">To
          <input type="date" value={filters.date_to}
            onChange={e => setFilters(f => ({ ...f, date_to: e.target.value }))} className={`${input} block mt-1`} />
        </label>
        <label className="flex items-center gap-2 text-sm text-gray-600 h-9">
          <input type="checkbox" checked={filters.include_hidden}
            onChange={e => setFilters(f => ({ ...f, include_hidden: e.target.checked }))} />
          Include cancelled
        </label>
        <button type="submit" className="bg-gray-100 text-sm font-semibold px-4 py-2 rounded-lg hover:bg-gray-200">Apply</button>
      </form>
      <p className="text-xs text-gray-400 mb-3">Dates default to 30 days either side of today; ZOCS allows at most 92 days at once.</p>

      <div className="flex flex-wrap gap-2 mb-4">
        {['', ...STATUSES].map(s => (
          <button key={s || 'all'} onClick={() => { setPage(1); setStatus(s); }}
            className={`text-sm px-3 py-1 rounded-full capitalize ${status === s ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
            {s ? s.replace(/_/g, ' ') : 'All'}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-gray-500">No ZOCS appointments match. Try a wider date range, or clear the status filter.</p>
      ) : (
        <div className="bg-white border border-gray-200 rounded overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
              <tr>
                <th className="text-left px-3 py-2">Appointment</th>
                <th className="text-left px-3 py-2">Customer</th>
                <th className="text-left px-3 py-2">Visit</th>
                <th className="text-left px-3 py-2">ZOCS status</th>
                <th className="text-left px-3 py-2">Child</th>
                <th className="text-right px-3 py-2">In app</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map(row => {
                const child = row.children[0];
                return (
                  <tr key={`${row.booking_code}-${row.appointment_id}`} className="align-top">
                    <td className="px-3 py-2">
                      <span className="block font-mono">{row.booking_code}</span>
                      {row.display_source && <Pill>{row.display_source}</Pill>}
                    </td>
                    <td className="px-3 py-2">
                      <span className="block font-medium">{row.customer_name || 'Unnamed'}</span>
                      <span className="block text-xs text-gray-400">
                        {row.phone || 'No phone'}{row.has_app_account ? ' · has app account' : ''}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <span className="block">{fmtDate(row.scheduled_date)}</span>
                      <span className="block text-xs text-gray-400">
                        {row.scheduled_time || 'No slot'}{row.pincode ? ` · ${row.pincode}` : ''}
                      </span>
                    </td>
                    <td className="px-3 py-2 space-x-1">
                      {row.operational_status && <Pill tone="blue">{row.operational_status.replace(/_/g, ' ')}</Pill>}
                      {row.customer_status && row.customer_status !== 'booked' && (
                        <Pill tone={row.customer_status === 'cancelled' ? 'pink' : 'gray'}>{row.customer_status.replace(/_/g, ' ')}</Pill>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-500">
                      {child ? [child.gender, child.size].filter(Boolean).join(' · ') || '—' : '—'}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {row.synced ? (
                        <div className="flex flex-col items-end gap-1">
                          <span className="text-green-700 font-medium">✓ Synced</span>
                          {row.phone && row.scheduled_date && (
                            <button onClick={() => setPreviewRow(row)} className="text-xs text-gray-600 hover:underline">Check payments</button>
                          )}
                        </div>
                      ) : !syncable(row) ? (
                        <span className="text-xs text-gray-400">
                          {row.sync_blocker || (row.phone ? 'Unreadable date in ZOCS' : 'No valid phone in ZOCS')}
                        </span>
                      ) : (
                        <button onClick={() => setPreviewRow(row)}
                          className="bg-zoddle-green text-white text-xs font-semibold px-3 py-1.5 rounded-lg hover:opacity-90">
                          Review &amp; sync
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {data && (data.total > PAGE_SIZE || page > 1) && (
        <div className="flex items-center justify-end gap-3 mt-3 text-sm">
          <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}
            className="px-3 py-1 border border-gray-200 rounded disabled:opacity-40">Previous</button>
          <span className="text-gray-500">Page {page} of {Math.max(1, Math.ceil(data.total / PAGE_SIZE))}</span>
          <button disabled={!data.has_more} onClick={() => setPage(p => p + 1)}
            className="px-3 py-1 border border-gray-200 rounded disabled:opacity-40">Next</button>
        </div>
      )}

      {previewRow && (
        <PreviewModal row={previewRow} onClose={() => setPreviewRow(null)} onSynced={load} />
      )}
    </section>
  );
}
